// Real-time Market Pricing Constants
// Update these values to reflect current market conditions
//
// IMPORTANT: This file separates Market Rates vs Protocol Rates:
// - Market Rates: For buying/selling nICP on exchanges (varies with supply/demand)
// - Protocol Rates: For staking ICP → nICP and redeeming nICP → ICP (fixed by protocol)

export const MARKET_PRICES = {
  // Base token prices in USD
  ICP: 4.80,               // USD per ICP
  nICP: 5.171410717748713, // USD per nICP (1 ICP = 0.92818 nICP, ≈ +7.7% premium)
  
  // Other token prices
  ckBTC: 30000,
  ETH: 3000,
  PANDA: 0.0027,
  ckUSDT: 1,
  GLDT: 8,
  
  // Airdrop token prices
  WTN: 0.15,               // USD per WTN
};

// Protocol-specific constants - MUST be declared before usage
export const PROTOCOL_CONSTANTS = {
  // Protocol staking rates (from WaterNeuron protocol)
  ICP_TO_NICP_STAKING_RATE: 0.87550507,  // 1 ICP = 0.87550507 nICP (protocol rate)
  N_ICP_NAV_OVER_ICP_PROTOCOL: 1.14233,  // 1 nICP redeemable for 1.14233 ICP (NAV)
  
  // Airdrop terms
  WTN_PER_100_ICP: 50,     // WTN tokens per 100 ICP staked
  
  // Unstaking periods
  UNSTAKING_PERIOD_MONTHS: 6,
  
  // Staking APY constant
  STAKING_APY: 15.0,       // % (15% APY constant)
  
  // Default APR ranges for projections (legacy)
  DEFAULT_APR_MIN: 6,      // %
  DEFAULT_APR_MAX: 12,     // %
  
  // Trading fees and slippage
  MARKET_SLIPPAGE_FEE: 0.45, // % (0.3-0.6% range, using mid-point)
  MARKET_VOLATILITY_RANGE: 0.3, // % (additional range for market volatility)
};

// Rate derivation function - single source of truth for all rates
export const deriveRates = ({ 
  icpPrice, 
  nIcpPrice, 
  icpToNICP_Quote, 
  nIcpNavOverIcp 
}) => {
  // Use live DEX quote if available, otherwise derive from prices
  const icpToNICP_market = (typeof icpToNICP_Quote === 'number')
    ? icpToNICP_Quote
    : icpPrice / nIcpPrice;

  const nIcpToIcp_market = 1 / icpToNICP_market;
  const icpToNICP_staking = 1 / nIcpNavOverIcp;

  return { 
    icpToNICP_market, 
    nIcpToIcp_market, 
    icpToNICP_staking, 
    nIcpNavOverIcp 
  };
};

// Current rates using the derivation function (now PROTOCOL_CONSTANTS is available)
export const CURRENT_RATES = deriveRates({
  icpPrice: MARKET_PRICES.ICP,
  nIcpPrice: MARKET_PRICES.nICP,
  icpToNICP_Quote: undefined, // Set this if you have a direct DEX quote
  nIcpNavOverIcp: PROTOCOL_CONSTANTS.N_ICP_NAV_OVER_ICP_PROTOCOL
});

// Legacy DERIVED_RATES for backward compatibility
export const DERIVED_RATES = {
  // Market rates (for trading/swapping)
  nIcpToIcp_Market: CURRENT_RATES.nIcpToIcp_market,
  icpToNICP_Market: CURRENT_RATES.icpToNICP_market,
  
  // Protocol rates (for staking/redemption)
  icpToNICP_Staking: CURRENT_RATES.icpToNICP_staking,
  nIcpToIcp_Protocol: CURRENT_RATES.nIcpNavOverIcp,
  
  // Premium/discount calculations
  marketPremium: ((CURRENT_RATES.nIcpToIcp_market - 1) * 100),
  protocolPremium: ((CURRENT_RATES.nIcpNavOverIcp - CURRENT_RATES.nIcpToIcp_market) / CURRENT_RATES.nIcpToIcp_market * 100),
};

// Helper functions for common calculations
export const calculateWTNValueInICP = (icpAmount) => {
  const wtnTokens = (icpAmount / 100) * PROTOCOL_CONSTANTS.WTN_PER_100_ICP;
  const wtnValueUSD = wtnTokens * MARKET_PRICES.WTN;
  return wtnValueUSD / MARKET_PRICES.ICP;
};

export const calculateTokenValueUSD = (ticker, amount) => {
  const price = MARKET_PRICES[ticker] || 0;
  return amount * price;
};

// Protocol NAV rate (uses actual protocol values)
export const getNIcpNavOverIcp = () => {
  return PROTOCOL_CONSTANTS.N_ICP_NAV_OVER_ICP_PROTOCOL; // Protocol redemption rate
};

export const getStakingRate = () => {
  return PROTOCOL_CONSTANTS.ICP_TO_NICP_STAKING_RATE; // Protocol staking rate: 1 ICP → 0.87550507 nICP
};

// Decision Mode calculation function - normalize to ICP equivalent
export const calcDecision = (icpAmount, rates, wtnPer100, wtnUsd, icpUsd) => {
  // Staking route
  const nIcpStake = icpAmount * rates.icpToNICP_staking;
  const stakeIcpEq = nIcpStake * rates.nIcpToIcp_market; // display in ICP equivalent
  const wtnIcpBonus = (wtnPer100 * wtnUsd / icpUsd) * (icpAmount / 100);
  const stakeTotalIcp = stakeIcpEq + wtnIcpBonus;

  // Market route
  const nIcpMkt = icpAmount * rates.icpToNICP_market;
  const mktIcpEq = nIcpMkt * rates.nIcpToIcp_market; // ≈ icpAmount, but keep consistent

  const diffIcp = stakeTotalIcp - mktIcpEq;
  const diffPct = (diffIcp / mktIcpEq) * 100;

  return {
    stake: { 
      nIcp: nIcpStake, 
      icpEq: stakeIcpEq, 
      wtnIcpBonus, 
      totalIcp: stakeTotalIcp,
      wtnCount: (icpAmount / 100) * wtnPer100
    },
    market: { 
      nIcp: nIcpMkt, 
      icpEq: mktIcpEq, 
      totalIcp: mktIcpEq 
    },
    diff: { 
      icp: diffIcp, 
      pct: diffPct 
    }
  };
};

// Exact gain calculation for Wait & Redeem vs Immediate Swap
export const calcExactGain = (nIcpAmount, rates) => {
  // Immediate: nICP -> ICP (market rate with slippage/fees)
  const slippageFactor = (100 - PROTOCOL_CONSTANTS.MARKET_SLIPPAGE_FEE) / 100;
  const immediateIcpOut = nIcpAmount * rates.nIcpToIcp_market * slippageFactor;
  
  // Delayed: nICP -> ICP (protocol NAV rate)
  const delayedIcpOut = nIcpAmount * rates.nIcpNavOverIcp;
  
  // Exact gain = Delayed - Immediate
  const exactGainIcp = delayedIcpOut - immediateIcpOut;
  const exactGainPct = (exactGainIcp / immediateIcpOut) * 100;
  
  // Calculate APY for 6-month delayed unstake
  // Assuming we start with equivalent ICP and end up with delayedIcpOut
  const timeInYears = 0.5; // 6 months
  const growthFactor = delayedIcpOut / immediateIcpOut;
  const annualizedYield = Math.pow(growthFactor, 1/timeInYears) - 1;
  const apyPercent = annualizedYield * 100;

  return {
    immediate: immediateIcpOut,
    delayed: delayedIcpOut,
    gainIcp: exactGainIcp,
    gainPct: exactGainPct,
    apy: apyPercent,
    isPositive: exactGainIcp > 0,
    isSignificant: exactGainIcp > 0.01 // More than 0.01 ICP
  };
};
